import { Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from '@shared/auth/ProtectedRoute';
import { RoleGuard } from '@shared/auth/RoleGuard';
import { LoginScreen } from './screens/Login/LoginScreen';
import { CallbackScreen } from './screens/Callback/CallbackScreen';
import { AdminShell } from './shell/AdminShell';
import { SitesPane } from './screens/Sites/SitesPane';
import { CommissionPane } from './screens/Commission/CommissionPane';
import { SettingsPane } from './screens/Settings/SettingsPane';

/**
 * ONE role, and deliberately not the support console's three.
 *
 * `apps/support-console` admits platform_admin, support_l2 and
 * support_l3, because reading across sites and running vetted runbooks
 * is L2/L3 work. This app changes which sites exist and can destroy a
 * site's live data — blast radius, not seniority, is what separates
 * them. The same asymmetry is enforced twice more behind this guard
 * (`edge_authz::_has_admin_role`, `admin.require_platform_admin`) and
 * structurally by which container mounts the router; this is the
 * fourth layer, and the only one an attacker can simply skip by
 * calling the API directly. It exists to keep the UI honest, not to
 * be the control.
 */
const ADMIN_ROLES = ['nexsoss:platform_admin'] as const;

function AccessDenied() {
  return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <h1>Access denied</h1>
      <p style={{ color: 'var(--nx-muted)' }}>
        Platform administration requires the <code>platform_admin</code> role. Support engineers
        (L2/L3) should use the platform support console.
      </p>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/callback" element={<CallbackScreen />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <RoleGuard allowedRoles={[...ADMIN_ROLES]} fallback={<AccessDenied />}>
              <AdminShell />
            </RoleGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="sites" replace />} />
        <Route path="sites" element={<SitesPane />} />
        <Route path="commission" element={<CommissionPane />} />
        <Route path="settings" element={<SettingsPane />} />
      </Route>
    </Routes>
  );
}
